import { useCallback, useEffect, useState } from "react";
import { Button, Flex, Modal, Table, TablePaginationConfig } from "antd";
import styled from "styled-components";
import { TParamsUser } from "services/UserManagement";
import { User } from "services/auth";
import { SorterResult } from "antd/es/table/interface";
import moment from "moment";
import {
  getCommentReportsApi,
  putCommentReportsApi,
} from "services/user-report";

import { ReactNode, useMemo } from "react";
import { deleteCommentApi } from "services/comment";
import { API_URL } from "configAxios";

export const parseMentions = (input: string) => {
  const mentionRegex = /\{@\}\[(.+?)\]\((\d+)\)/g;
  let text = "";
  const mentions: {
    userId: number;
    name: string;
    start: number;
    end: number;
  }[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = mentionRegex.exec(input))) {
    const [full, name, userId] = match;
    const start = match.index;
    text += input.substring(lastIndex, start);
    const mentionStart = text.length;
    text += name;
    mentions.push({
      userId: Number(userId),
      name,
      start: mentionStart,
      end: mentionStart + name.length,
    });

    lastIndex = start + full.length;
  }
  text += input.substring(lastIndex);

  return { text, mentions };
};

type Props = {
  content: string;
};

const MentionText = ({ content }: Props) => {
  const { text, mentions } = useMemo(() => parseMentions(content), [content]);

  if (!mentions || mentions.length === 0) {
    return <span>{text}</span>;
  }

  const parts: ReactNode[] = [];
  let lastIndex = 0;

  mentions.forEach((mention, idx) => {
    if (lastIndex < mention.start) {
      parts.push(
        <span key={`text-${idx}-${lastIndex}`}>
          {text.substring(lastIndex, mention.start)}
        </span>
      );
    }
    parts.push(
      <span
        key={`mention-${idx}-${mention.start}`}
        style={{ color: "blue", fontWeight: "600" }}
      >
        {text.substring(mention.start, mention.end)}
      </span>
    );
    lastIndex = mention.end;
  });

  if (lastIndex < text.length) {
    parts.push(<span key={"text-end"}>{text.substring(lastIndex)}</span>);
  }

  return <span>{parts}</span>;
};

const initFilter = {
  page: 1,
  limit: 10,
  total: 0,
};

type Report = {
  createdAt: string;
  description: string;
  id: number;
  photo: string;
  reason: string;
  reported: User;
  user: User;
};

const CommentReportManagement = () => {
  const [data, setData] = useState<Report[]>([]);
  const [params, setParams] = useState<TParamsUser>(initFilter);
  const [isLoading, setIsLoading] = useState(false);

  const fetchData = useCallback(async (params?: TParamsUser) => {
    setIsLoading(true);
    try {
      const { data } = await getCommentReportsApi(params);
      setData(data.reports);
      setParams((pre) => ({
        ...pre,
        total: data.total,
        page: params?.page ?? pre.page,
      }));
    } catch (error: any) {
      Modal.error({
        content: error.response?.data.message || "Fetch data failed",
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleChangeTable = useCallback(
    (
      { current, pageSize }: TablePaginationConfig,
      _: any,
      { order, field }: SorterResult
    ) => {
      if (!current || !pageSize) return;
      fetchData({
        ...params,
        page: current!,
        limit: pageSize!,
        order: order ? (order === "ascend" ? "asc" : "desc") : "",
        sortBy: (field as string) || "",
      });
    },
    [fetchData, params]
  );

  const onDeleteComment = useCallback(
    (recordId: number, commentId: number) => {
      Modal.confirm({
        title: "Delete Comment",
        content: "Are you sure you want to delete this comment?",
        okText: "Confirm",
        cancelText: "Cancel",
        onOk: async () => {
          await deleteCommentApi(commentId);
          await putCommentReportsApi(recordId, true);
          fetchData();
        },
      });
    },
    [fetchData]
  );

  const columns = [
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      render: ({ firstName, lastName }: User) => (
        <p>{`${firstName || ""} ${lastName || ""}`}</p>
      ),
    },
    {
      title: "User Reported",
      dataIndex: "comment",
      key: "comment",
      render: (v: any) => <MentionText content={v.content} />,
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Photo",
      dataIndex: "photo",
      key: "photo",
      render: (text: string) => (
        <img
          src={`${API_URL}/api/uploads/${text}`}
          alt="avatar user"
          style={{ width: 50, height: 50 }}
        />
      ),
    },
    {
      title: "Created at",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      render: (text: string) => <p>{moment(text).format("MM/DD/YYYY")}</p>,
    },
    {
      title: "",
      width: 130,
      dataIndex: "action",
      key: "creaactiontedAt",
      render: (_: any, r: any) => {
        return (
          <Button danger onClick={() => onDeleteComment(r.id, r.comment.id)}>
            Delete Comment
          </Button>
        );
      },
    },
  ];

  return (
    <WrapperUserManagement>
      <h1>User Report</h1>
      <Table<Report>
        loading={isLoading}
        dataSource={data}
        onChange={handleChangeTable as any}
        pagination={{
          current: params.page,
          pageSize: params.limit,
          total: params.total,
          showSizeChanger: false,
        }}
        columns={columns}
      />
    </WrapperUserManagement>
  );
};

export default CommentReportManagement;

const WrapperUserManagement = styled(Flex)`
  width: 100%;
  flex-direction: column;
  gap: 20px;
  &.ant-pagination {
    margin: 8px 0px !important;
  }
  > h1 {
    margin: 0;
  }
`;
